import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, effect, inject, Injectable, signal, type Signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, type ActivatedRoute } from '@angular/router';
import { createChatNotificationViewKey, type NotificationView } from '@frontendlib/notifications';
import { filter } from 'rxjs';

export interface HostedChatNotificationView {
  readonly accountId: string | null;
  readonly spaceId: string | null;
  readonly channelId: string | null;
  readonly readable: boolean;
}

export function notificationSettingsOpen(route: ActivatedRoute): boolean {
  return route.outlet === 'settings' || route.children.some(notificationSettingsOpen);
}

/** The mounted conversation owns a lease; destroying an older view cannot clear a newer one. */
@Injectable({ providedIn: 'root' })
export class HostedNotificationViewService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mounted = signal<HostedChatNotificationView | null>(null);
  private readonly mountedContext = signal<string | null>(null);
  private readonly presentation = signal<{
    readonly owner: symbol;
    readonly contextKey: string;
    readonly visible: Signal<boolean>;
  } | null>(null);
  private readonly visible = signal(this.document.visibilityState === 'visible');
  private readonly settingsOpen = signal(notificationSettingsOpen(this.router.routerState.root));
  private owner: symbol | null = null;
  private revision = 0;
  private readonly currentView = signal<NotificationView | null>(null);

  public constructor() {
    const visibility = (): void => {
      const visible = this.document.visibilityState === 'visible';
      if (visible !== this.visible()) this.invalidateHint();
      this.visible.set(visible);
    };
    this.document.addEventListener('visibilitychange', visibility);
    this.destroyRef.onDestroy(() => {
      this.invalidateHint();
      this.document.removeEventListener('visibilitychange', visibility);
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const open = notificationSettingsOpen(this.router.routerState.root);
        if (open !== this.settingsOpen()) this.invalidateHint();
        this.settingsOpen.set(open);
      });
    effect(() => {
      const mounted = this.mounted();
      const readable = this.visible() && !this.settingsOpen() && mounted?.readable === true;
      const revision = ++this.revision;
      untracked(() => this.currentView.set(null));
      if (!mounted?.accountId || !mounted.spaceId || !mounted.channelId) return;
      void createChatNotificationViewKey(mounted.accountId, mounted.spaceId, mounted.channelId)
        .then((key) => {
          if (revision === this.revision && key) this.currentView.set({ scopeId: mounted.accountId, key, readable });
        })
        .catch(() => undefined);
    });
  }

  public readonly notificationView = computed(() => {
    const view = this.currentView();
    const presentation = this.presentation();
    if (!view?.readable || !presentation || presentation.contextKey !== this.mountedContext()) return view;
    return presentation.visible() ? view : { ...view, readable: false };
  });

  /** A layout owns its context's visibility, independently of the latest conversation lease. */
  public mountPresentation(contextKey: string, visible: Signal<boolean>): { close(): void } {
    const owner = Symbol('notification-presentation');
    this.presentation.set({ owner, contextKey, visible });
    return {
      close: () => {
        if (this.presentation()?.owner === owner) this.presentation.set(null);
      },
    };
  }

  /** contextKey labels presentation only; recipient scope and opaque keys still come from the conversation. */
  public mount(contextKey: string | null = null): { update(view: HostedChatNotificationView): void; close(): void } {
    const owner = Symbol('mounted-chat-notification-view');
    this.owner = owner;
    this.mountedContext.set(contextKey);
    this.invalidateHint();
    this.mounted.set(null);
    return {
      update: (view) => {
        if (this.owner !== owner) return;
        this.invalidateHint();
        this.mounted.set({ ...view });
      },
      close: () => {
        if (this.owner !== owner) return;
        this.owner = null;
        this.mountedContext.set(null);
        this.invalidateHint();
        this.mounted.set(null);
      },
    };
  }

  private invalidateHint(): void {
    this.revision += 1;
    this.currentView.set(null);
  }
}
